import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, Trash2, Plus, Minus, Send, Loader2, Edit2, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CartItem, SelectedAddon } from "@/hooks/usePublicCart";

export type { CartItem, SelectedAddon };

interface TabletCartProps {
  items: CartItem[];
  subtotal: number;
  onUpdateQuantity: (index: number, quantity: number) => void;
  onRemove: (index: number) => void;
  onUpdateNotes?: (index: number, notes: string) => void;
  onCheckout: () => void;
  isSubmitting?: boolean;
  disabled?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function TabletCart({
  items,
  subtotal,
  onUpdateQuantity,
  onRemove,
  onUpdateNotes,
  onCheckout,
  isSubmitting,
  disabled,
  isOpen: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: TabletCartProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState("");
  
  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = controlledOnOpenChange ?? setInternalOpen;
  
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  
  const handleEditNote = (index: number, currentNote: string) => {
    setEditingNoteIndex(index);
    setEditingNoteValue(currentNote || "");
  };
  
  const handleSaveNote = (index: number) => {
    onUpdateNotes?.(index, editingNoteValue);
    setEditingNoteIndex(null);
    setEditingNoteValue("");
  };
  
  const handleCancelEdit = () => {
    setEditingNoteIndex(null);
    setEditingNoteValue("");
  };

  if (items.length === 0) return null;

  return (
    <>
      {/* Floating cart bar */}
      <div className="fixed bottom-0 left-24 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-800 px-6 py-4 z-30">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <button className="flex items-center gap-3 text-white">
                <div className="relative">
                  <ShoppingCart className="w-6 h-6" />
                  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {itemCount}
                  </span>
                </div>
                <span className="text-lg">
                  {itemCount} {itemCount === 1 ? "item" : "itens"}
                </span>
                <span className="text-2xl font-bold text-primary">
                  R$ {subtotal.toFixed(2).replace(".", ",")}
                </span>
              </button>
            </SheetTrigger>

            <SheetContent
              side="right"
              className="w-full sm:max-w-lg bg-slate-900 border-slate-800"
            >
              <SheetHeader>
                <SheetTitle className="text-white text-2xl">Seu Pedido</SheetTitle>
              </SheetHeader>

              <div className="mt-6 space-y-4 flex-1 overflow-auto">
                {items.map((item, index) => (
                  <div
                    key={`${item.id}-${index}`}
                    className="bg-slate-800/50 rounded-xl p-4 flex gap-4"
                  >
                    {/* Image */}
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-slate-700 flex items-center justify-center text-3xl">
                        🍽️
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white truncate">
                        {item.name}
                      </h4>
                      {item.selectedAddons && item.selectedAddons.length > 0 && (
                        <p className="text-sm text-primary truncate">
                          {item.selectedAddons.map(a => `+ ${a.name}`).join(", ")}
                        </p>
                      )}
                      
                      {/* Editable notes */}
                      {editingNoteIndex === index ? (
                        <div className="space-y-2 mt-2">
                          <input
                            type="text"
                            value={editingNoteValue}
                            onChange={(e) => setEditingNoteValue(e.target.value)}
                            placeholder="Observação..."
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleSaveNote(index)}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Confirmar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-10 border-slate-600"
                              onClick={handleCancelEdit}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 mt-1">
                          {item.notes ? (
                            <p className="text-sm text-slate-400 truncate italic flex-1">
                              {item.notes}
                            </p>
                          ) : (
                            <p className="text-sm text-slate-500 truncate flex-1">
                              Sem observação
                            </p>
                          )}
                          {onUpdateNotes && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-slate-400 hover:text-white shrink-0"
                              onClick={() => handleEditNote(index, item.notes || "")}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      )}
                      
                      <p className="text-primary font-bold mt-1">
                        R$ {(item.price * item.quantity).toFixed(2).replace(".", ",")}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col items-end gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-slate-400 hover:text-red-400"
                        onClick={() => onRemove(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>

                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 border-slate-600"
                          onClick={() =>
                            onUpdateQuantity(index, item.quantity - 1)
                          }
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-bold text-white">
                          {item.quantity}
                        </span>
                        <Button
                          size="icon"
                          className="h-8 w-8"
                          onClick={() =>
                            onUpdateQuantity(index, item.quantity + 1)
                          }
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="border-t border-slate-800 pt-4 mt-4 space-y-4">
                <div className="flex justify-between text-lg">
                  <span className="text-slate-400">Subtotal</span>
                  <span className="font-bold text-white">
                    R$ {subtotal.toFixed(2).replace(".", ",")}
                  </span>
                </div>

                <Button
                  size="lg"
                  className="w-full h-14 text-lg font-semibold"
                  onClick={() => {
                    onCheckout();
                    setIsOpen(false);
                  }}
                  disabled={disabled || isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Enviar Pedido
                </Button>
              </div>
            </SheetContent>
          </Sheet>

          <Button
            size="lg"
            className="h-14 px-8 text-lg font-semibold"
            onClick={onCheckout}
            disabled={disabled || isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Send className="w-5 h-5 mr-2" />
            )}
            Enviar Pedido
          </Button>
        </div>
      </div>
    </>
  );
}
